import { faGlobe, faGripVertical, faProjectDiagram, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import type { DraggableSyntheticListeners } from '@dnd-kit/core/dist/hooks/useDraggable';
import type { Timestamp } from '@framework/Timestamp';
import { buildDateTimeArgs } from '@framework/i18n/buildDateTimeArgs';
import { Tooltip } from '@framework/ui/atoms/Tooltip';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { MenuButtons } from '@workspace/view-model-folder/ui/ViewModelItem/MenuButtons';
import { Link } from 'react-router';
import type { ViewModelId } from 'balus-common';
import { classNames } from '@framework/utils';

type Props = {
  viewModelId: ViewModelId;
  publicReadable: boolean;
  individualPermissionConfigured: boolean;
  name: string;
  isLocked: boolean;
  updatedAt: Timestamp | null;
  showEditMenu: boolean;
  noMenu: boolean;
  onEdit(): void;
  onCopy(): void;
  showConfigureIndividualPermissions: boolean;
  onConfigureIndividualPermissions(): void;
  onDelete(): void;
  onMoveViewModel(): void;
  onMoveToOtherWorkspace(): void;
  dragListeners?: DraggableSyntheticListeners;
  isRestricted: boolean;
};

const Content: React.FC<Props> = ({
  viewModelId,
  publicReadable,
  individualPermissionConfigured,
  name,
  isLocked,
  updatedAt,
  showEditMenu,
  noMenu,
  onDelete,
  onCopy,
  showConfigureIndividualPermissions,
  onConfigureIndividualPermissions,
  onEdit,
  onMoveViewModel,
  onMoveToOtherWorkspace,
  dragListeners,
  isRestricted,
}: Props) => {
  const { t } = useTranslation();
  // タイトルコンテンツ（Link と div で共通）
  const titleContent = (
    <>
      {isLocked && <FontAwesomeIcon icon={faLock} className="pr-2" />}
      {name}
      {updatedAt && (
        <div className="text-3xs text-gray-500">
          {t('common.label.lastUpdatedAt', { dateTime: t('common.dateTimeFormat', buildDateTimeArgs(updatedAt)) })}
        </div>
      )}
    </>
  );

  return (
    <div
      className={classNames(
        'mb-2 flex h-12 w-full items-center rounded border border-gray-500 bg-white',
        isRestricted ? 'cursor-not-allowed' : 'hover:bg-gray-200'
      )}
    >
      {/* アイコンエリア */}
      <div
        className={classNames(
          'relative flex h-full w-16 items-center justify-center rounded-l text-center text-white',
          isRestricted ? 'bg-gray-400' : 'bg-brand'
        )}
      >
        <span className="text-2xl">
          <FontAwesomeIcon icon={faProjectDiagram} />
        </span>
        {publicReadable ? (
          <span className="absolute bottom-0 right-0 inline-block size-4 -translate-x-2 -translate-y-3 text-xl">
            <Tooltip tooltip={t('workspace.viewModelItem.publicBadge')} placement="bottom">
              <FontAwesomeIcon icon={faGlobe} />
            </Tooltip>
          </span>
        ) : individualPermissionConfigured ? (
          <span className="absolute bottom-0 right-0 inline-block size-4 -translate-x-2 -translate-y-3 text-xl">
            <Tooltip tooltip={t('workspace.viewModelItem.permissionBadge')} placement="bottom">
              <FontAwesomeIcon icon={faUser} />
            </Tooltip>
          </span>
        ) : null}
      </div>

      {/* タイトルエリア - 制限時はLinkではなくdivを使用 */}
      {isRestricted ? (
        <div className="flex-1 truncate px-2 text-xl font-bold text-gray-400">{titleContent}</div>
      ) : (
        <Link
          className="flex-1 truncate px-2 text-xl font-bold hover:text-blue-800"
          to={UserPagePathBuilder.viewModelPage(viewModelId)}
        >
          {titleContent}
        </Link>
      )}

      {/* メニューエリア - 制限時は非表示 */}
      {!isRestricted &&
        (noMenu ? null : showEditMenu ? (
          <div className="flex items-center justify-center px-2">
            <>
              <MenuButtons
                onEdit={onEdit}
                onCopy={onCopy}
                showConfigureIndividualPermissions={showConfigureIndividualPermissions}
                onConfigureIndividualPermissions={onConfigureIndividualPermissions}
                onDelete={onDelete}
                onMoveViewModel={onMoveViewModel}
                onMoveToOtherWorkspace={onMoveToOtherWorkspace}
              />
              {dragListeners && (
                <button {...dragListeners} className="px-2 hover:bg-gray-400">
                  <FontAwesomeIcon icon={faGripVertical} />
                </button>
              )}
            </>
          </div>
        ) : (
          <div className="flex items-center justify-center px-2">
            <Tooltip tooltip={t('viewModel.copy.label')} placement={'bottom'}>
              <div
                className="cursor-pointer rounded border border-solid border-brand bg-white px-2 py-1 font-bold text-brand hover:bg-gray-200"
                onClick={onCopy}
              >
                <FontAwesomeIcon icon={faCopy} />
              </div>
            </Tooltip>
          </div>
        ))}
    </div>
  );
};

export const ItemContent: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  if (props.isRestricted) {
    return (
      <Tooltip tooltip={t('workspace.viewModelItem.restrictedTooltip')} placement="top">
        <Content {...props} />
      </Tooltip>
    );
  }

  return <Content {...props} />;
};
